/**
 * A2 — Captura da imagem da nota/cupom.
 *
 * Renderiza UI mínima de upload: área clicável + `<input type="file"
 * capture="environment">` (no mobile abre câmera traseira; desktop abre
 * file picker). Ao selecionar arquivo:
 *   1. Estado "uploading".
 *   2. Chama `expenseFromImage(file, note)` que comprime client-side
 *      e POSTa pra `/api/expense/from-image`.
 *   3. Sucesso → invoca `onExtracted(data.extracted)`.
 *   4. Erro    → toast com a frase humanizada que o backend devolve
 *      (Fase 2 já cobre os 6 error_codes da A1).
 *
 * Props:
 *   - `onExtracted(extracted)` — callback chamado com o payload OK.
 *   - `onClose()`              — fecha o modal/sheet.
 *   - `note` (opcional)        — comentário do usuário enviado junto
 *     com a imagem (vira o campo `note` do upload). A2 usa "" implícito;
 *     A3 passa o texto que estava digitado no input do chat.
 *
 * Sem dependência nova. `lucide-react` (já no projeto) cuida do ícone.
 */
import { useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { expenseFromImage } from "@/services/expenseImage";


export default function ReceiptCapture({ onExtracted, onClose, note }) {
  const inputRef = useRef(null);
  const [state, setState] = useState("idle");
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Preview discreto enquanto o upload roda. Object URL é revogado
    // após processamento pra não vazar memória.
    let preview = null;
    try {
      preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
    } catch {
      /* objetos URL podem falhar em browsers exóticos */
    }

    setState("uploading");
    const result = await expenseFromImage(file, note);
    if (preview) {
      try { URL.revokeObjectURL(preview); } catch { /* */ }
    }

    if (result?.ok && result.extracted) {
      setState("success");
      onExtracted?.(result.extracted);
      return;
    }

    // Erro: a frase pra mostrar vem do backend (já humanizada pela Fase 2
    // quando flag HUMANIZED_ERRORS_ENABLED estiver ligada; senão, vem o
    // `error` interno bruto, que ainda é melhor que silêncio).
    setState("error");
    setPreviewUrl(null);
    const code = result?.error_code || "EXTRACTION_FAILED";
    const message = result?.error || "Não consegui ler essa imagem.";
    toast.error(message, {
      description: `Código: ${code}`,
      duration: 6000,
    });
    // Reset do input pra permitir re-selecionar o MESMO arquivo.
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div
      data-testid="receipt-capture"
      className="bg-[#0d1220] border border-white/[0.08] rounded-2xl p-6 safe-modal-pb w-full max-w-md"
    >
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-xl font-bold text-white">
          Despesa por foto
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="p-2 -mr-2 rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-colors"
          aria-label="Fechar"
          data-testid="receipt-capture-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <p className="text-sm text-slate-400 mb-4">
        Tire uma foto do cupom ou escolha uma imagem. Eu extraio valor,
        data e estabelecimento — você confirma antes de registrar.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleSelect}
        data-testid="receipt-capture-input"
        disabled={state === "uploading"}
      />

      {/* Área clicável principal — abre o picker / câmera */}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={state === "uploading"}
        className="w-full flex flex-col items-center justify-center gap-3 py-10 px-4 rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] hover:bg-white/[0.04] transition-colors disabled:opacity-50 disabled:cursor-wait"
        data-testid="receipt-capture-trigger"
      >
        {state === "uploading" ? (
          <>
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
            <span className="text-sm text-slate-300">Lendo a imagem…</span>
          </>
        ) : (
          <>
            <Camera className="w-8 h-8 text-cyan-400" />
            <span className="text-sm font-medium text-white">
              Tocar pra abrir câmera ou galeria
            </span>
            <span className="text-xs text-slate-500">
              JPG, PNG, HEIC ou WebP
            </span>
          </>
        )}
      </button>

      {previewUrl && state === "uploading" && (
        <div className="mt-4">
          <img
            src={previewUrl}
            alt="Pré-visualização"
            className="w-full max-h-48 object-contain rounded-lg border border-white/[0.06]"
            data-testid="receipt-capture-preview"
          />
        </div>
      )}
    </div>
  );
}
